import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { X, Printer } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * FabricRollQRModal — preview + print a QR label for a fabric roll.
 * QR payload: NYLSU-FR-<roll_number>  (stable, safe, resolvable via /qr/resolve).
 */
export default function FabricRollQRModal({ roll, onClose }) {
  const { t } = useI18n();
  const [material, setMaterial] = useState(null);
  const [payload, setPayload] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/fabric-rolls/${roll.id}/qr`);
        if (!alive) return;
        setPayload(r.data.payload);
        setMaterial(r.data.material);
      } catch (e) {
        if (!alive) return;
        setPayload(`NYLSU-FR-${roll.roll_number || roll.id}`);
        if (e?.response?.status !== 403) toast.error(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, [roll.id]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:bg-white print:p-0"
      onClick={onClose}
      data-testid="fabric-roll-qr-modal"
    >
      <div
        className="w-full max-w-md bg-white border shadow-xl print:border-0 print:shadow-none print:max-w-none"
        style={{ borderColor: "var(--nylsu-border-strong)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between print:hidden" style={{ borderColor: "var(--nylsu-border)" }}>
          <div className="overline">{t("qr.rollTitle")}</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border inline-flex items-center gap-2"
              style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }}
              data-testid="fabric-roll-qr-print"
            >
              <Printer size={12} weight="light" /> {t("common.print")}
            </button>
            <button type="button" onClick={onClose} className="p-2 text-[#8F8D84] hover:text-[#0A0A0A]"><X size={14} weight="light" /></button>
          </div>
        </div>
        <div
          className="p-8 flex flex-col items-center gap-4"
          data-testid="fabric-roll-qr-content"
        >
          <div style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.3em", fontSize: 12 }}>NYLSU</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, textAlign: "center", lineHeight: 1.2 }}>
            {material?.name || t("qr.fabric")}
          </div>
          {material?.color_name && (
            <div className="text-xs uppercase tracking-[0.15em] text-[#5C5A54]">{material.color_name}</div>
          )}
          {payload && <QRCodeSVG value={payload} size={200} level="M" includeMargin={false} />}
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.15em] text-[#8F8D84]">{t("rolls.rollNumber")}</div>
            <div className="font-mono text-lg" data-testid="fabric-roll-qr-number">{roll.roll_number}</div>
          </div>
          {roll.dye_lot && (
            <div className="text-xs text-[#5C5A54]">
              <span className="uppercase tracking-[0.15em] text-[10px]">{t("rolls.dyeLot")}</span>{" "}
              <span className="font-mono">{roll.dye_lot}</span>
            </div>
          )}
          <div className="text-[10px] font-mono text-[#8F8D84]" data-testid="fabric-roll-qr-payload">{payload}</div>
        </div>
      </div>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="fabric-roll-qr-content"], [data-testid="fabric-roll-qr-content"] * { visibility: visible !important; }
          [data-testid="fabric-roll-qr-content"] { position: absolute; left: 20mm; top: 20mm; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}
